import React, { useCallback, useMemo } from "react";
import { Row } from "@tanstack/react-table";
import { DataTable } from "ui-components/data-table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { usePlanningPickupList } from "../hooks/usePlanningPickupList";
import { useDrawerTableContainerHeight } from "../hooks/useDrawerTableContainerHeight";
import {
  getOutboundPlanningShipmentColumns,
  getRowCanExpandFreight,
  OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningShipmentColumns";
import PlanningFreightListTotalsBar, {
  PLANNING_PICKUP_TOTALS_METRICS,
} from "./planningFreightListTotalsBar";
import {
  getOutboundPlanningItemColumns,
  OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningItemColumns";
import { ShipmentListCategory } from "../shipmentListTypes";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import "../outboundPlanningList.scss";

const MAIN_ROW_HEIGHT = 44;
const ITEM_ROW_HEIGHT = 36;

const PICKUP_ITEM_COLUMNS_HIDDEN_BY_DEFAULT = [
  ...OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
  "itemLocation",
  "lastChanged",
  "lastChangeUser",
  "updatedAt",
  "addedAt",
];

interface PickupsFreightListPanelProps {
  category: ShipmentListCategory;
  originTerminalNumber: number;
  destinationTerminalNumbers: number[];
  fetchId: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
}

const PickupsFreightListPanel: React.FC<PickupsFreightListPanelProps> = ({
  category,
  originTerminalNumber,
  destinationTerminalNumbers,
  fetchId,
  onPickupNumberClick,
}) => {
  const { containerRef, tableHeight } = useDrawerTableContainerHeight(24);
  const { data, isLoading, isError } = usePlanningPickupList(
    category,
    originTerminalNumber,
    destinationTerminalNumbers,
    fetchId,
    true
  );

  const shipmentColumnPrefs = useDataTableColumnPreferences({
    instanceKey: `outbound-planning-${category}-shipments`,
    columnOptions: OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMN_OPTIONS,
    hiddenByDefault: OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
  });
  const itemColumnPrefs = useDataTableColumnPreferences({
    instanceKey: `outbound-planning-${category}-items`,
    columnOptions: OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
    hiddenByDefault: PICKUP_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
  });

  const shipmentColumns = useMemo(
    () =>
      getOutboundPlanningShipmentColumns({
        category: "pickup",
        originTerminalNumber,
        onPickupNumberClick,
      }),
    [originTerminalNumber, onPickupNumberClick]
  );

  const itemColumns = useMemo(() => getOutboundPlanningItemColumns(), []);

  const renderSubComponent = useCallback(
    (row: Row<PlanningFreightProbill>) => (
      <div className="outbound-planning-freight-list-panel__sub-table">
        <DataTable
          columns={itemColumns}
          data={row.original.items ?? []}
          {...itemColumnPrefs}
          showColumnSelector
          columnSelectorOptions={OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS}
          enableColumnResize
          enableColumnReorder
          enableRowSelection={false}
          responsive
          autoHeight={false}
          shrinkToContent
          rowHeight={ITEM_ROW_HEIGHT}
          noResultsText="No items for this pickup"
        />
      </div>
    ),
    [itemColumns, itemColumnPrefs]
  );

  return (
    <div className="outbound-planning-freight-list-panel">
      <div ref={containerRef} className="outbound-planning-freight-list-panel__table">
        {isError ? (
          <XGSErrorMessage>Error loading pickups. Please try again.</XGSErrorMessage>
        ) : isLoading ? (
          <div className="outbound-planning-freight-list-panel__loading">
            <Loading isLoading />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="outbound-planning-freight-list-panel__empty">No pickups found.</div>
        ) : (
          <DataTable
            columns={shipmentColumns}
            data={data}
            isLoading={false}
            {...shipmentColumnPrefs}
            showColumnSelector
            columnSelectorOptions={OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMN_OPTIONS}
            enableColumnResize
            enableColumnReorder
            enableRowSelection={false}
            responsive
            sorting
            enableMultiSort
            filters
            renderSubComponent={renderSubComponent}
            getRowCanExpand={getRowCanExpandFreight}
            stickyLeadingColumnId="expand"
            autoHeight={false}
            listHeightOverride={tableHeight ?? 400}
            rowHeight={MAIN_ROW_HEIGHT}
            stickyHeaders
            renderTableFooter={({ rows }) => (
              <PlanningFreightListTotalsBar
                rows={rows}
                rowHeight={MAIN_ROW_HEIGHT}
                metrics={PLANNING_PICKUP_TOTALS_METRICS}
              />
            )}
            customHeaderRowClass="outbound-planning-shipment-table__header-row"
          />
        )}
      </div>
    </div>
  );
};

export default PickupsFreightListPanel;
